import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, ExternalLink } from "lucide-react";
import { Spinner } from "@/shared/components/ui/spinner";
import { formatCurrency, formatDate } from "@/shared/lib/formatters";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { ROUTES } from "@/constants/routes";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { rentalService } from "../services/rental.service";
import { RentalStatusBadge } from "./RentalStatusBadge";
import { RENTAL_TYPE_LABELS } from "./RentalTypeBadge";
import type { RentalResponseDTO } from "../types/rental.types";

interface ClientRentalsSectionProps {
  clientId: number;
}

interface SectionState {
  clientId: number;
  rentals: RentalResponseDTO[];
  totalElements: number;
  error: string | null;
}

export const ClientRentalsSection = ({
  clientId,
}: ClientRentalsSectionProps) => {
  const { isAdmin } = useAuth();
  const [state, setState] = useState<SectionState | null>(null);
  const loaded = state?.clientId === clientId ? state : null;
  const loading = loaded === null;
  const rentals = loaded?.rentals ?? [];
  const totalElements = loaded?.totalElements ?? 0;
  const error = loaded?.error ?? null;

  // A tela de aluguéis é distinta por role — o deep link segue a rota
  // que o role consegue abrir (/alugueis é ADMIN, /ver-alugueis é USER).
  const rentalRoute = isAdmin ? ROUTES.RENTALS : ROUTES.RENTALS_VIEW;

  useEffect(() => {
    const controller = new AbortController();
    const search = isAdmin ? rentalService.adminSearch : rentalService.search;
    search(
      {
        clientIds: [clientId],
        page: 0,
        size: 50,
        sortBy: "rentedAt",
        sortDirection: "DESC",
      },
      { signal: controller.signal },
    )
      .then((response) => {
        if (controller.signal.aborted) return;
        setState({
          clientId,
          rentals: response.content,
          totalElements: response.totalElements,
          error: null,
        });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setState({
          clientId,
          rentals: [],
          totalElements: 0,
          error: getApiErrorMessage(
            err,
            "Não foi possível carregar os aluguéis do cliente.",
          ),
        });
      });
    return () => controller.abort();
  }, [clientId, isAdmin]);

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <ClipboardList className="w-4 h-4" /> Aluguéis
        {!loading && !error && <span>({totalElements})</span>}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-2 text-muted-foreground">
          <Spinner className="w-4 h-4" />
          <span className="text-sm">Carregando aluguéis...</span>
        </div>
      ) : error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : rentals.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nenhum aluguel para este cliente.
        </p>
      ) : (
        <>
          {totalElements > rentals.length && (
            <p className="text-xs text-muted-foreground">
              Mostrando os {rentals.length} mais recentes.
            </p>
          )}
          <ul className="space-y-1.5">
            {rentals.map((rental) => (
              <li key={rental.id}>
                <Link
                  to={`${rentalRoute}?rentalId=${rental.id}`}
                  title="Abrir o aluguel"
                  className="flex items-center justify-between gap-3 rounded-md bg-background px-3 py-2 text-sm transition-colors hover:bg-accent"
                >
                  <div className="flex flex-col gap-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">#{rental.id}</span>
                      <RentalStatusBadge status={rental.status} />
                      <span className="font-medium">
                        {formatCurrency(rental.totalValue)}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {RENTAL_TYPE_LABELS[rental.rentalType]} · Contrato{" "}
                      {rental.contractNumber ?? "—"} · Locação:{" "}
                      {formatDate(rental.rentedAt)}
                    </span>
                  </div>
                  <ExternalLink
                    className="w-3.5 h-3.5 shrink-0 text-primary"
                    aria-hidden="true"
                  />
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};
